import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "~/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        ghost: cn(
          "text-text-primary border border-border-alpha-10 bg-white/10",
          "hover:bg-brand-25 active:bg-brand-25",
          "transition-all ease-out duration-200"
        ),
        link: "text-primary underline-offset-4 hover:underline",
        yellow: cn(
          "bg-gradient-to-r from-[#fff6b5] to-brand-500 text-text-primary",
          "border border-border-alpha-10",
          "shadow-[inset_-2px_-2px_6px_0px_rgba(0,0,0,0.06)]",
          "hover:opacity-90 transition-opacity"
        ),
        primary: cn(
          "text-text-primary opacity-100",
          "[background:linear-gradient(180deg,#FFECA5_0%,#FFFAC5_7.35%,#FFF984_20.29%,#FFF984_66.89%,#F5EE56_85.56%,#FFE05F_97.12%)]",
          "[&:hover,&:active]:[background:linear-gradient(180deg,#FFEE8F_0%,#FFFAC5_7.35%,#FFF984_20.29%,#FFF984_66.89%,#F5EE56_85.56%,#FFE05F_97.12%)]",
          "[&:hover,&:active]:opacity-70",
          "transition-all ease-out duration-300 active:duration-0"
        ),
        dark: cn(
          "bg-gradient-to-b from-[#333333] to-base-black to-15%",
          "border border-white/5 text-white",
          "hover:from-[#444444] hover:to-neutral-900",
          "transition-all ease-out duration-200"
        ),
        glass: cn(
          "bg-white/40 border border-white text-neutral-900 backdrop-blur-sm",
          "hover:bg-white/50",
          "transition-all ease-out duration-200"
        ),
        darkGlass: cn(
          "bg-[rgba(0,0,0,0.8)] border border-[rgba(0,0,0,0.05)] text-white backdrop-blur-sm",
          "hover:bg-[rgba(0,0,0,0.9)]",
          "transition-all ease-out duration-200"
        ),
        tabTriggerActive: cn(
          "bg-gradient-to-r from-[#fff6b5] to-brand-500 text-text-primary font-semibold rounded-xl",
          "transition-all hover:opacity-90"
        ),
        tabTriggerInactive: cn(
          "bg-background-base text-text-primary font-medium rounded-xl",
          "transition-all hover:bg-[#ecebe3]"
        ),
        carouselNav: cn(
          "bg-neutral-50 text-text-primary border border-neutral-300 rounded-xl",
          "backdrop-blur-sm shadow-sm hover:bg-neutral-100 transition-all"
        ),

        // Legacy sizes for backward compatibility
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
      },
      size: {
        large:
          "font-bold text-[20px] leading-[24px] rounded-[20px] px-[24px] py-[16px] gap-[8px] h-[56px] [&_svg:not([class*='size-'])]:size-5",
        big: "font-bold text-[16px] leading-[24px] rounded-[18px] px-[20px] py-[8px] gap-[4px] h-[48px] [&_svg:not([class*='size-'])]:size-4",
        medium:
          "font-semibold text-[14px] leading-[20px] rounded-[16px] px-[16px] py-[8px] gap-[4px] h-[40px] [&_svg:not([class*='size-'])]:size-4",
        small:
          "font-semibold text-[14px] leading-[20px] rounded-[12px] px-[12px] py-[8px] gap-[4px] h-[32px] [&_svg:not([class*='size-'])]:size-4",
        tiny: "font-medium text-[12px] leading-[18px] rounded-[8px] p-[8px] gap-[2px] h-[28px] [&_svg:not([class*='size-'])]:size-3",

        // Legacy sizes for backward compatibility
        default:
          "h-9 px-4 py-2 gap-2 rounded-md text-sm font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 gap-2 text-base font-medium [&_svg:not([class*='size-'])]:size-4 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  id,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      id={id}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
